import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { Flex, Tooltip } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { GroupTarget, TargetCheck } from '../api/types';
import { formatMoney, formatNumber } from '../format';
import { brand } from '../theme';

export type TargetKind = keyof GroupTarget;

export const percentOf = (x: number | null | undefined) =>
  x === null || x === undefined ? '—' : `${formatNumber(x * 100, 1)} %`;

/** The target as read: at least a rate, at most a cost. */
export function useTargetLabel() {
  const { t } = useTranslation();
  return (kind: TargetKind, value: number) =>
    kind === 'maxCostPerTonne'
      ? t('targets.atMost', { value: formatMoney(value) })
      : t('targets.atLeast', { value: percentOf(value) });
}

/**
 * A figure with its target (FR-PIL-11): a check mark when it is met, a cross when it is not,
 * nothing when no target is set or the figure is not known; the tooltip states the target.
 */
export function TargetValue({
  check,
  kind,
  children,
}: {
  check: TargetCheck | null;
  kind: TargetKind;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const label = useTargetLabel();
  if (!check) return <>{children}</>;
  const target = label(kind, check.target);
  const title =
    check.met === null
      ? t('targets.unknown', { target })
      : t(check.met ? 'targets.met' : 'targets.missed', { target });
  return (
    <Tooltip title={title}>
      <Flex gap={6} align="center" justify="flex-end" style={{ display: 'inline-flex' }}>
        <span>{children}</span>
        {check.met === true && (
          <CheckCircleFilled style={{ color: brand.success }} aria-label={title} />
        )}
        {check.met === false && (
          <CloseCircleFilled style={{ color: brand.danger }} aria-label={title} />
        )}
      </Flex>
    </Tooltip>
  );
}
